import { useRef, useState } from 'react';
import { StatusChip } from './JurisdictionBadge';
import { INFORMATIONAL_NOTICE } from './checklistContent';
import { RequirementsDialog } from './RequirementsDialog';
import { countsSentence } from './statusLabels';
import type { TaxSetupStatus } from './types';

interface Props {
  status: TaxSetupStatus | null;
  loading?: boolean;
  /** The read failed: the panel says so and offers Retry. */
  failed?: boolean;
  onRetry?: () => void;
  headingLevel?: 3 | 4;
}

/** The summary of the tax setup: the overall status, the counts ("3 complete, 2 need attention"), the plain statement that it is informational, and Review requirements. */
export function TaxSetupStatusPanel({ status, loading, failed, onRetry, headingLevel = 3 }: Props) {
  const [reviewing, setReviewing] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const Heading = headingLevel === 3 ? 'h3' : 'h4';

  function close() {
    setReviewing(false);
    trigger.current?.focus();
  }

  return (
    <section className="ts ts-status" aria-labelledby="ts-status-title" data-testid="tax-setup-status">
      <Heading id="ts-status-title" className="ts-status__title">
        Tax setup status
      </Heading>
      {failed ? (
        <>
          <p className="ts-status__error" role="alert">
            The tax setup status could not be loaded. Nothing is shown as complete.
          </p>
          {onRetry && (
            <button type="button" className="ts-btn ts-btn--outline" onClick={onRetry}>
              Retry
            </button>
          )}
        </>
      ) : !status || loading ? (
        <p className="ts-note" role="status">
          Loading the tax setup status…
        </p>
      ) : (
        <>
          <p className="ts-status__line">
            <StatusChip status={status.status} /> <span data-testid="tax-setup-counts">{countsSentence(status.counts)}</span>
          </p>
          <p className="ts-note" data-testid="tax-setup-informational">
            {INFORMATIONAL_NOTICE}
          </p>
          <button ref={trigger} type="button" className="ts-btn ts-btn--outline" onClick={() => setReviewing(true)}>
            Review requirements
          </button>
          {reviewing && <RequirementsDialog status={status} onClose={close} />}
        </>
      )}
    </section>
  );
}
